/**
 * RuleTable — tableau éditable d'une table de règles CCQ datées (début / fin / source). Batch #14.298.
 * `columns` décrit les cellules ; `versioned` ajoute « Nouvelle version » (l'ancienne ligne est fermée la veille).
 */
import { useEffect, useRef } from "react";
import { Plus, Trash2, CopyPlus } from "lucide-react";
import { cellCls, Btn, thCls, today, addDays } from "../_shared";

const FAR_PAST = "1900-01-01";

export function MultiSelect({ value = [], options, onChange, size = 3, testId }) {
  return (
    <select multiple size={size} className={`${cellCls} min-w-[150px]`} value={value} onChange={(e) => onChange(Array.from(e.target.selectedOptions).map((o) => o.value))} data-testid={testId}>
      {options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
    </select>
  );
}

export const getPath = (row, key) => key.split(".").reduce((o, k) => (o == null ? undefined : o[k]), row);
export const setPath = (row, key, v) => {
  const [head, ...rest] = key.split(".");
  return rest.length ? { ...row, [head]: setPath(row[head] || {}, rest.join("."), v) } : { ...row, [head]: v };
};

export function Cell({ col, row, onChange, testId }) {
  const v = getPath(row, col.key);
  const opts = typeof col.options === "function" ? col.options(row) : col.options || [];
  if (col.show && !col.show(row)) return <span className="text-slate-300 text-xs">—</span>;
  switch (col.type) {
    case "select":
      return (
        <select className={`${cellCls} ${col.width || "min-w-[120px]"}`} value={v ?? ""} onChange={(e) => onChange(col.key, e.target.value)} data-testid={testId}>
          {col.blank !== undefined && <option value="">{col.blank}</option>}
          {opts.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
      );
    case "multi": return <MultiSelect value={v || []} options={opts} onChange={(val) => onChange(col.key, val)} testId={testId} />;
    case "bool": return <input type="checkbox" checked={!!v} onChange={(e) => onChange(col.key, e.target.checked)} className="w-4 h-4 accent-amber-600" data-testid={testId} />;
    case "number": return <input type="number" step={col.step || "0.01"} min="0" className={`${cellCls} text-right ${col.width || "w-24"}`} value={v ?? ""} onChange={(e) => onChange(col.key, e.target.value)} placeholder={col.placeholder} data-testid={testId} />;
    case "date": return <input type="date" className={`${cellCls} w-36`} value={v && v !== FAR_PAST ? v : ""} onChange={(e) => onChange(col.key, e.target.value || null)} title={v === FAR_PAST ? "Depuis toujours" : undefined} data-testid={testId} />;
    default: return <input className={`${cellCls} ${col.width || "min-w-[130px]"}`} value={v ?? ""} onChange={(e) => onChange(col.key, e.target.value)} placeholder={col.placeholder} data-testid={testId} />;
  }
}

const DATE_COLS = [
  { key: "effective_from", label: "En vigueur du", type: "date" },
  { key: "effective_to", label: "Jusqu'au", type: "date" },
  { key: "source", label: "Source", type: "text", placeholder: "Convention 2025-2029…", width: "min-w-[140px]" },
];

export default function RuleTable({ rows, onChange, columns, emptyRow, testId, addLabel = "Ajouter une ligne", versioned = false, hint, empty, focusId = "" }) {
  const cols = [...columns, ...DATE_COLS];
  const focusRef = useRef(null);
  useEffect(() => {
    if (focusId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      focusRef.current.querySelector("input, select")?.focus({ preventScroll: true });
    }
  }, [focusId, rows.length]);
  const update = (i, k, v) => onChange(rows.map((r, j) => (j === i ? setPath(r, k, v) : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const newVersion = (i) => {
    const t = today();
    const { id, ...copy } = rows[i];
    onChange([...rows.map((r, j) => (j === i ? { ...r, effective_to: r.effective_to || addDays(t, -1) } : r)), { ...copy, effective_from: t, effective_to: null, source: "" }]);
  };
  const expired = (r) => r.effective_to && r.effective_to < today();
  return (
    <div className="space-y-2" data-testid={testId}>
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
      {rows.length === 0 ? (
        <p className="text-xs text-slate-500 bg-white border border-dashed border-slate-300 rounded-xl p-5 text-center" data-testid={`${testId}-empty`}>{empty || "Aucune règle. Ajoutez une ligne ou importez un fichier."}</p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs">
            <thead><tr>{cols.map((c) => <th key={c.key} className={`${thCls} whitespace-nowrap`}>{c.label}</th>)}<th className={thCls}></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => {
                const focused = !!focusId && r.id === focusId;
                return (
                <tr key={r.id || i} ref={focused ? focusRef : undefined} className={`${expired(r) ? "opacity-50" : ""} ${focused ? "bg-rose-50 ring-2 ring-inset ring-rose-400 animate-pulse [animation-iteration-count:3]" : ""}`} data-testid={`${testId}-row-${i}`} data-focused={focused || undefined}>
                  {cols.map((c) => <td key={c.key} className="px-1 py-1 align-top"><Cell col={c} row={r} onChange={(k, v) => update(i, k, v)} testId={`${testId}-${c.key}-${i}`} /></td>)}
                  <td className="px-1 py-1 whitespace-nowrap align-top">
                    {versioned && <button type="button" onClick={() => newVersion(i)} className="p-1 rounded text-amber-700 hover:bg-amber-50" title="Nouvelle version (ferme celle-ci la veille)" data-testid={`${testId}-version-${i}`}><CopyPlus className="w-3.5 h-3.5" /></button>}
                    <button type="button" onClick={() => remove(i)} className="p-1 rounded text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`${testId}-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Btn tone="ghost" icon={Plus} onClick={() => onChange([...rows, { effective_from: today(), effective_to: null, source: "", ...emptyRow() }])} testId={`${testId}-add`}>{addLabel}</Btn>
    </div>
  );
}
